import { supabase } from '../lib/supabase';

export const analyticsService = {
  /**
   * Get post analytics for a specific brand
   */
  async getPostAnalytics(brandId, options = {}) {
    const { timeRange = 30 } = options;
    
    try {
      const { data, error } = await supabase?.from('post_analytics')?.select(`
          *,
          post:scheduled_posts(id, content, scheduled_date)
        `)?.eq('brand_id', brandId)?.gte('recorded_at', new Date(Date.now() - timeRange * 24 * 60 * 60 * 1000)?.toISOString())?.order('recorded_at', { ascending: false });

      if (error) throw error;

      // Convert to camelCase
      return data?.map(item => ({
        id: item?.id,
        postId: item?.post_id,
        brandId: item?.brand_id,
        platform: item?.platform,
        reach: item?.reach,
        impressions: item?.impressions,
        engagement: item?.engagement,
        likes: item?.likes,
        comments: item?.comments,
        shares: item?.shares,
        clicks: item?.clicks,
        engagementRate: parseFloat(item?.engagement_rate),
        recordedAt: item?.recorded_at,
        post: item?.post
      })) || [];
    } catch (error) {
      console.error('Error fetching post analytics:', error);
      throw error;
    }
  },

  /**
   * Get aggregated metrics for analytics dashboard
   */
  async getAggregatedMetrics(brandId) {
    try {
      // Validate brandId is provided
      if (!brandId) {
        return {
          totalReach: 0,
          totalEngagement: 0,
          avgEngagementRate: 0,
          totalLikes: 0,
          totalComments: 0,
          totalShares: 0
        };
      }

      const { data, error } = await supabase?.from('post_analytics')?.select('reach, engagement, likes, comments, shares')?.eq('brand_id', brandId)?.gte('recorded_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString());

      if (error) throw error;

      if (!data || data?.length === 0) {
        return {
          totalReach: 0,
          totalEngagement: 0,
          avgEngagementRate: 0,
          totalLikes: 0,
          totalComments: 0,
          totalShares: 0
        };
      }

      const totals = data?.reduce((acc, item) => ({
        reach: acc?.reach + (item?.reach || 0),
        engagement: acc?.engagement + (item?.engagement || 0),
        likes: acc?.likes + (item?.likes || 0),
        comments: acc?.comments + (item?.comments || 0),
        shares: acc?.shares + (item?.shares || 0)
      }), { reach: 0, engagement: 0, likes: 0, comments: 0, shares: 0 });

      return {
        totalReach: totals?.reach,
        totalEngagement: totals?.engagement,
        avgEngagementRate: totals?.reach > 0 ? (totals?.engagement / totals?.reach) * 100 : 0,
        totalLikes: totals?.likes,
        totalComments: totals?.comments,
        totalShares: totals?.shares
      };
    } catch (error) {
      console.error('Error fetching aggregated metrics:', error);
      throw error;
    }
  },

  /**
   * Get follower growth data
   */
  async getFollowerGrowth(brandId, options = {}) {
    const { months = 6 } = options;
    
    try {
      const { data, error } = await supabase?.from('follower_growth')?.select(`
          *,
          social_account:social_accounts(platform, account_name)
        `)?.eq('brand_id', brandId)?.gte('recorded_at', new Date(Date.now() - months * 30 * 24 * 60 * 60 * 1000)?.toISOString())?.order('recorded_at', { ascending: true });

      if (error) throw error;

      return data?.map(item => ({
        id: item?.id,
        brandId: item?.brand_id,
        socialAccountId: item?.social_account_id,
        followerCount: item?.follower_count,
        growthAmount: item?.growth_amount,
        growthPercentage: parseFloat(item?.growth_percentage),
        recordedAt: item?.recorded_at,
        platform: item?.social_account?.platform,
        accountName: item?.social_account?.account_name
      })) || [];
    } catch (error) {
      console.error('Error fetching follower growth:', error);
      throw error;
    }
  },

  /**
   * Record new analytics data
   */
  async recordAnalytics(analyticsData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('post_analytics')?.insert({
          post_id: analyticsData?.postId,
          brand_id: analyticsData?.brandId,
          platform: analyticsData?.platform,
          reach: analyticsData?.reach || 0,
          impressions: analyticsData?.impressions || 0,
          engagement: analyticsData?.engagement || 0,
          likes: analyticsData?.likes || 0,
          comments: analyticsData?.comments || 0,
          shares: analyticsData?.shares || 0,
          clicks: analyticsData?.clicks || 0
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        postId: data?.post_id,
        brandId: data?.brand_id,
        platform: data?.platform,
        reach: data?.reach,
        engagement: data?.engagement,
        engagementRate: parseFloat(data?.engagement_rate)
      };
    } catch (error) {
      console.error('Error recording analytics:', error);
      throw error;
    }
  }
};